// Printer service for handling receipt and invoice printing
import { formatCurrency } from '../lib/utils'
export interface PrintItem {
  name: string
  quantity: number
  price: number
  total: number
  hsn?: string
  mrp?: number
  discount?: number
  gstRate?: number
}

export interface PrintData {
  type: 'receipt' | 'invoice'
  billNumber: string
  date: string
  customerName?: string
  customerPhone?: string
  customerGST?: string
  customerAddress?: string
  customerEmail?: string
  customerState?: string
  items: PrintItem[]
  subtotal: number
  taxAmount: number
  totalAmount: number
  paymentMethod?: string
  amountReceived?: number
  change?: number
}

export interface BusinessInfo {
  name: string
  address: string
  phone: string
  email?: string
  gstNumber?: string
  website?: string
  state?: string
}

export interface PrintSettings {
  format: 'a4' | 'thermal' | 'a5'
  showLogo: boolean
  showTerms: boolean
  autoPrint: boolean
  copiesCount: number
  itemsPerPage?: number
}

class PrinterService {
  private businessInfo: BusinessInfo = {
    name: '',
    address: '',
    phone: '',
    email: '',
    gstNumber: ''
  }

  private settings: PrintSettings = {
    format: 'thermal',
    showLogo: true,
    showTerms: true,
    autoPrint: false,
    copiesCount: 1,
    itemsPerPage: 15
  }

  // Update business information
  setBusinessInfo(info: BusinessInfo) {
    this.businessInfo = { ...info }
  }

  // Update print settings
  setSettings(settings: Partial<PrintSettings>) {
    this.settings = { ...this.settings, ...settings }
  }

  // Helper method to calculate tax summary by HSN and GST rate
  private calculateTaxSummary(items: PrintItem[]) {
    const summaryMap = new Map<string, {
      hsn: string
      taxableAmount: number
      gstRate: number
      taxAmount: number
    }>()

    items.forEach(item => {
      const hsn = item.hsn || '-'
      const gstRate = item.gstRate || 18
      const taxableAmount = item.price * item.quantity
      const taxAmount = item.total - taxableAmount

      const key = `${hsn}_${gstRate}`

      if (summaryMap.has(key)) {
        const existing = summaryMap.get(key)!
        existing.taxableAmount += taxableAmount
        existing.taxAmount += taxAmount
      } else {
        summaryMap.set(key, {
          hsn,
          taxableAmount,
          gstRate,
          taxAmount
        })
      }
    })

    return Array.from(summaryMap.values()).sort((a, b) => {
      if (a.hsn !== b.hsn) return a.hsn.localeCompare(b.hsn)
      return a.gstRate - b.gstRate
    })
  }

  // Helper method to determine if transaction is inter-state
  private isInterState(businessState?: string, customerState?: string): boolean {
    if (!businessState || !customerState) return false
    // Normalize state names for comparison
    const normalizeState = (state: string) => state.trim().toLowerCase().replace(/\s+/g, '')
    return normalizeState(businessState) !== normalizeState(customerState)
  }

  // Helper method to convert numbers to words (Indian style)
  private numberToWords(num: number): string {
    if (num === 0) return 'Zero'

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']

    const convertLessThanThousand = (n: number): string => {
      if (n === 0) return ''
      if (n < 10) return ones[n] || ''
      if (n < 20) return teens[n - 10] || ''
      if (n < 100) return (tens[Math.floor(n / 10)] || '') + (n % 10 !== 0 ? ' ' + (ones[n % 10] || '') : '')
      return (ones[Math.floor(n / 100)] || '') + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '')
    }

    let intPart = Math.floor(num)
    const decPart = Math.round((num - intPart) * 100)

    let result = ''

    if (intPart >= 10000000) {
      const crores = Math.floor(intPart / 10000000)
      result += convertLessThanThousand(crores) + ' Crore '
      intPart %= 10000000
    }

    if (intPart >= 100000) {
      const lakhs = Math.floor(intPart / 100000)
      result += convertLessThanThousand(lakhs) + ' Lakh '
      intPart %= 100000
    }

    if (intPart >= 1000) {
      const thousands = Math.floor(intPart / 1000)
      result += convertLessThanThousand(thousands) + ' Thousand '
      intPart %= 1000
    }

    if (intPart > 0) {
      result += convertLessThanThousand(intPart)
    }

    result = result.trim() + ' Rupees'

    if (decPart > 0) {
      result += ' and ' + convertLessThanThousand(decPart) + ' Paise'
    }

    return result.trim()
  }

  // Generate thermal receipt HTML
  private generateThermalReceiptHTML(data: PrintData): string {
    const { businessInfo, settings } = this
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Receipt - ${data.billNumber}</title>
        <style>
          @media print {
            @page {
              margin: 0;
              size: 80mm auto;
            }
            body { margin: 0; }
          }
          
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.2;
            margin: 0;
            padding: 10px;
            width: 70mm;
            max-width: 70mm;
          }
          
          .center { text-align: center; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          .large { font-size: 14px; }
          .small { font-size: 10px; }
          
          .header {
            text-align: center;
            margin-bottom: 10px;
            border-bottom: 1px dashed #000;
            padding-bottom: 8px;
          }
          
          .business-name {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 2px;
          }
          
          .contact-info {
            font-size: 10px;
            margin-bottom: 1px;
          }
          
          .bill-info {
            margin: 8px 0;
            border-bottom: 1px dashed #000;
            padding-bottom: 5px;
          }
          
          .items-table {
            width: 100%;
            margin: 8px 0;
          }
          
          .items-header {
            border-bottom: 1px solid #000;
            font-weight: bold;
            padding-bottom: 2px;
            margin-bottom: 4px;
          }
          
          .item-row {
            margin-bottom: 2px;
            display: flex;
            justify-content: space-between;
          }
          
          .item-name {
            flex: 1;
            margin-right: 5px;
          }
          
          .item-qty-price {
            text-align: right;
            white-space: nowrap;
          }
          
          .totals {
            margin-top: 8px;
            border-top: 1px dashed #000;
            padding-top: 5px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
          }
          
          .grand-total {
            border-top: 1px solid #000;
            padding-top: 3px;
            margin-top: 3px;
            font-weight: bold;
            font-size: 13px;
          }
          
          .payment-info {
            margin-top: 8px;
            border-top: 1px dashed #000;
            padding-top: 5px;
          }
          
          .footer {
            text-align: center;
            margin-top: 10px;
            border-top: 1px dashed #000;
            padding-top: 8px;
            font-size: 10px;
          }
          
          .terms {
            margin-top: 8px;
            font-size: 9px;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <!-- Business Header -->
        <div class="header">
          <div class="business-name">${businessInfo.name}</div>
          ${businessInfo.address ? `<div class="contact-info">${businessInfo.address.replace(/\n/g, '<br>')}</div>` : ''}
          ${businessInfo.phone ? `<div class="contact-info">Ph: ${businessInfo.phone}</div>` : ''}
          ${businessInfo.email ? `<div class="contact-info">${businessInfo.email}</div>` : ''}
          ${businessInfo.gstNumber ? `<div class="contact-info">GSTIN: ${businessInfo.gstNumber}</div>` : ''}
        </div>

        <!-- Bill Information -->
        <div class="bill-info">
          <div class="total-row">
            <span>Bill No:</span>
            <span class="bold">${data.billNumber}</span>
          </div>
          <div class="total-row">
            <span>Date:</span>
            <span>${data.date}</span>
          </div>
          ${data.customerName ? `
            <div class="total-row">
              <span>Customer:</span>
              <span>${data.customerName}</span>
            </div>
          ` : ''}
          ${data.customerPhone ? `
            <div class="total-row">
              <span>Phone:</span>
              <span>${data.customerPhone}</span>
            </div>
          ` : ''}
        </div>

        <!-- Items -->
        <div class="items-table">
          <div class="items-header">
            <div style="display: flex; justify-content: space-between;">
              <span>Item</span>
              <span>Qty x Rate = Total</span>
            </div>
          </div>
          
          ${data.items.map(item => `
            <div class="item-row">
              <div class="item-name">${item.name}</div>
            </div>
            <div class="item-row small" style="margin-left: 10px; margin-bottom: 4px;">
              <span></span>
              <span>${item.quantity} x ${formatCurrency(item.price)} = ${formatCurrency(item.total)}</span>
            </div>
          `).join('')}
        </div>

        <!-- Totals -->
        <div class="totals">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>${formatCurrency(data.subtotal)}</span>
          </div>
          <div class="total-row">
            <span>Tax (GST):</span>
            <span>${formatCurrency(data.taxAmount)}</span>
          </div>
          <div class="total-row grand-total">
            <span>TOTAL:</span>
            <span>${formatCurrency(data.totalAmount)}</span>
          </div>
        </div>

        <!-- Payment Information -->
        ${data.paymentMethod || data.amountReceived ? `
          <div class="payment-info">
            ${data.paymentMethod ? `
              <div class="total-row">
                <span>Payment:</span>
                <span class="bold">${data.paymentMethod.toUpperCase()}</span>
              </div>
            ` : ''}
            ${data.amountReceived ? `
              <div class="total-row">
                <span>Received:</span>
                <span>${formatCurrency(data.amountReceived)}</span>
              </div>
            ` : ''}
            ${data.change && data.change > 0 ? `
              <div class="total-row">
                <span>Change:</span>
                <span>${formatCurrency(data.change)}</span>
              </div>
            ` : ''}
          </div>
        ` : ''}

        <!-- Footer -->
        <div class="footer">
          <div>Thank You for Your Business!</div>
          <div>Visit Again! 🪔</div>
          ${businessInfo.website ? `<div class="small">${businessInfo.website}</div>` : ''}
        </div>

        <!-- Terms and Conditions -->
        ${settings.showTerms ? `
          <div class="terms">
            <div>Terms & Conditions:</div>
            <div>• Goods once sold will not be taken back</div>
            <div>• All disputes subject to local jurisdiction</div>
          </div>
        ` : ''}
      </body>
      </html>
    `
  }

  // Generate A5 invoice HTML (Landscape GST format)
  private generateA5InvoiceHTML(data: PrintData): string {
    const { businessInfo, settings } = this

    // Calculate items per page (configurable, default 15)
    const itemsPerPage = settings.itemsPerPage || 15
    const totalPages = Math.ceil(data.items.length / itemsPerPage)

    // Split items into pages
    const pages: PrintItem[][] = []
    for (let i = 0; i < data.items.length; i += itemsPerPage) {
      pages.push(data.items.slice(i, i + itemsPerPage))
    }

    // Calculate tax summary grouped by HSN and GST rate
    const taxSummary = this.calculateTaxSummary(data.items)

    // Determine if inter-state transaction (for IGST vs CGST/SGST)
    const isInterStateTxn = this.isInterState(businessInfo.state, data.customerState)

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Tax Invoice - ${data.billNumber}</title>
        <style>
          * {
            box-sizing: border-box;
          }

          @media print {
            @page {
              size: A5 landscape;
              margin: 8mm;
            }
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
              margin: 0;
              padding: 0;
            }
            .page {
              page-break-after: always;
              page-break-inside: avoid;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .no-print {
              display: none;
            }
          }

          @media screen {
            body {
              background: #f0f0f0;
              padding: 20px;
            }
            .page {
              margin-bottom: 20px;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
              background: white;
            }
          }

          body {
            font-family: Arial, sans-serif;
            font-size: 8pt;
            line-height: 1.2;
            margin: 0;
            padding: 0;
          }

          .page {
            width: 210mm;
            min-height: 148mm;
            max-height: 148mm;
            height: 148mm;
            padding: 8mm;
            position: relative;
            overflow: hidden;
          }

          .invoice-container {
            padding: 0;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
          }

          .tax-invoice-title {
            text-align: center;
            padding: 4px;
            font-weight: bold;
            font-size: 11pt;
            margin-bottom: 6px;
            letter-spacing: 1.5px;
            border-bottom: 2px solid black;
          }

          .header-section {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 8px;
            border-bottom: 2px solid black;
            padding-bottom: 4px;
            margin-bottom: 4px;
          }

          .company-info h1 {
            font-size: 13pt;
            font-weight: bold;
            margin: 0 0 2px 0;
          }

          .company-info p {
            font-size: 7pt;
            margin: 0;
            line-height: 1.3;
          }

          .invoice-details {
            text-align: right;
            font-size: 7pt;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .invoice-details > div {
            margin-bottom: 2px;
          }

          .original-tag {
            font-size: 6pt;
            margin-bottom: 2px;
          }

          .party-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6px;
            margin-bottom: 6px;
            font-size: 7pt;
            border-bottom: 1px solid #ccc;
            padding-bottom: 6px;
          }

          .party-box {
            border: 1px solid #333;
            padding: 5px;
            background: #f9f9f9;
          }

          .party-box strong {
            font-size: 7.5pt;
            display: block;
            margin-bottom: 2px;
            color: #000;
          }

          .party-box .detail-line {
            margin: 1px 0;
            line-height: 1.4;
          }

          .content-area {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }

          .items-section {
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 4px 0;
            font-size: 7pt;
          }

          .items-table th {
            background-color: #d0d0d0;
            border: 1px solid black;
            padding: 2px;
            text-align: center;
            font-size: 7pt;
            font-weight: bold;
          }

          .items-table td {
            border: 1px solid black;
            padding: 2px;
          }

          .text-center {
            text-align: center;
          }

          .text-right {
            text-align: right;
          }

          .text-left {
            text-align: left;
          }

          .spacer {
            flex: 1;
          }

          .bottom-section {
            display: grid;
            grid-template-columns: 1.5fr 1fr;
            gap: 6px;
            margin-top: auto;
            padding-top: 4px;
          }

          .page-number {
            text-align: right;
            font-size: 7pt;
            margin-top: 2px;
            font-style: italic;
          }

          .tax-summary {
            border: 1px solid black;
            padding: 4px;
            font-size: 6.5pt;
          }

          .tax-summary strong {
            font-size: 7pt;
          }

          .tax-summary-table {
            width: 100%;
            margin-top: 2px;
          }

          .tax-summary-table tr.header {
            font-weight: bold;
          }

          .tax-summary-table td {
            padding: 2px;
          }

          .totals-section {
            border: 1px solid black;
            padding: 4px;
          }

          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
            font-size: 7.5pt;
          }

          .grand-total {
            font-weight: bold;
            font-size: 8.5pt;
            border-top: 2px solid black;
            padding-top: 2px;
            margin-top: 2px;
          }

          .amount-in-words {
            margin-top: 4px;
            font-size: 6.5pt;
            font-style: italic;
            border-top: 1px solid #ccc;
            padding-top: 2px;
          }

          .page-footer {
            border-top: 2px solid black;
            padding-top: 4px;
            margin-top: auto;
            font-size: 7pt;
          }

          .page-footer-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .page-totals {
            font-size: 7pt;
          }

          .page-totals .total-line {
            display: flex;
            gap: 10px;
          }

          .footer-section {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 6px;
            margin-top: 4px;
            font-size: 6.5pt;
            border-top: 1px solid black;
            padding-top: 4px;
          }

          .footer-section strong {
            font-size: 7pt;
          }

          .signature-section {
            text-align: right;
            font-size: 7pt;
          }
        </style>
      </head>
      <body>
        ${pages.map((pageItems, pageIndex) => {
          const isLastPage = pageIndex === pages.length - 1
          const pageNumber = pageIndex + 1

          // Calculate running totals for current page
          const pageSubtotal = pageItems.reduce((sum, item) => {
            const taxableAmount = item.price * item.quantity
            return sum + taxableAmount
          }, 0)
          const pageTotalWithTax = pageItems.reduce((sum, item) => sum + item.total, 0)

          return `
        <div class="page">
          <div class="invoice-container">
            <!-- TAX INVOICE Title -->
            <div class="tax-invoice-title">
              TAX INVOICE
            </div>

            <!-- Header Section -->
            <div class="header-section">
              <div class="company-info">
                <h1>${businessInfo.name || 'YOUR COMPANY NAME'}</h1>
                <p>${businessInfo.address || '123, Business Street, Commercial Area, City - 600001'}</p>
                <p>Ph: ${businessInfo.phone || '+91 98765 43210'} | Email: ${businessInfo.email || 'info@company.com'}</p>
                <p><strong>GSTIN: ${businessInfo.gstNumber || '33XXXXX1234X1Z5'}</strong></p>
              </div>
              <div class="invoice-details">
                <div class="original-tag">ORIGINAL FOR RECIPIENT</div>
                <div><strong>Invoice:</strong> ${data.billNumber}</div>
                <div><strong>Date:</strong> ${data.date}</div>
                <div><strong>Payment:</strong> ${data.paymentMethod || 'Cash'}</div>
              </div>
            </div>

            <!-- Party Details (only on first page) -->
            ${pageIndex === 0 ? `
            <div class="party-section">
              <div class="party-box">
                <strong>Bill To:</strong>
                <div class="detail-line">${data.customerName || 'Walk-in Customer'}</div>
                ${data.customerAddress ? `<div class="detail-line">${data.customerAddress}</div>` : ''}
                ${data.customerPhone ? `<div class="detail-line">Phone: ${data.customerPhone}</div>` : ''}
                ${data.customerEmail ? `<div class="detail-line">Email: ${data.customerEmail}</div>` : ''}
                ${data.customerGST ? `<div class="detail-line"><strong>GSTIN:</strong> ${data.customerGST}</div>` : ''}
              </div>
              <div class="party-box">
                <strong>Ship To:</strong>
                <div class="detail-line">Same as Bill To</div>
              </div>
            </div>
            ` : ''}

            <!-- Content Area -->
            <div class="content-area">
              <div class="items-section">
                <!-- Items Table -->
                <table class="items-table">
                  <thead>
                    <tr>
                      <th style="width: 3%">No</th>
                      <th style="width: 28%">Description</th>
                      <th style="width: 6%">HSN</th>
                      <th style="width: 6%">Qty</th>
                      <th style="width: 8%">MRP</th>
                      <th style="width: 8%">Rate</th>
                      <th style="width: 6%">Disc%</th>
                      <th style="width: 9%">Taxable</th>
                      <th style="width: 6%">GST%</th>
                      <th style="width: 10%">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${pageItems.map((item, index) => {
                      const globalIndex = pageIndex * itemsPerPage + index
                      const itemDiscount = item.discount || 0
                      const itemGstRate = item.gstRate || 18
                      const itemMrp = item.mrp || item.price
                      const itemHsn = item.hsn || '-'
                      const taxableAmount = item.price * item.quantity
                      return `
                    <tr>
                      <td class="text-center">${globalIndex + 1}</td>
                      <td class="text-left">${item.name}</td>
                      <td class="text-center">${itemHsn}</td>
                      <td class="text-center">${item.quantity}</td>
                      <td class="text-right">${formatCurrency(itemMrp)}</td>
                      <td class="text-right">${formatCurrency(item.price)}</td>
                      <td class="text-center">${itemDiscount}%</td>
                      <td class="text-right">${formatCurrency(taxableAmount)}</td>
                      <td class="text-center">${itemGstRate}%</td>
                      <td class="text-right">${formatCurrency(item.total)}</td>
                    </tr>
                      `
                    }).join('')}
                    ${!isLastPage && pageItems.length < itemsPerPage ? `
                    <tr>
                      <td colspan="10" class="text-center" style="font-style: italic; padding: 8px;">Continued on next page...</td>
                    </tr>
                    ` : ''}
                  </tbody>
                </table>
              </div>

              <!-- Spacer to push content to bottom when few items -->
              ${isLastPage && pageItems.length < 5 ? '<div class="spacer"></div>' : ''}
              ${!isLastPage ? '<div class="spacer"></div>' : ''}

              <!-- Page Footer (for non-last pages) -->
              ${!isLastPage ? `
              <div class="page-footer">
                <div class="page-footer-content">
                  <div class="page-totals">
                    <div class="total-line">
                      <span><strong>Page Subtotal:</strong> ₹${formatCurrency(pageSubtotal)}</span>
                      <span style="margin-left: 15px;"><strong>Page Total (incl. Tax):</strong> ₹${formatCurrency(pageTotalWithTax)}</span>
                    </div>
                  </div>
                  <div>
                    <strong>Page ${pageNumber} of ${totalPages}</strong>
                  </div>
                </div>
              </div>
              ` : ''}

              <!-- Bottom Section (only on last page) -->
              ${isLastPage ? `
              <div class="bottom-section">
                <div class="tax-summary">
                  <strong>Tax Summary:</strong>
                  <table class="tax-summary-table">
                    <tr class="header">
                      <td class="text-right">Taxable</td>
                      <td class="text-center">Rate</td>
                      ${isInterStateTxn ? `
                        <td class="text-right">IGST</td>
                      ` : `
                        <td class="text-right">CGST</td>
                        <td class="text-right">SGST</td>
                      `}
                      <td class="text-right">Total Tax</td>
                    </tr>
                    ${taxSummary.map(tax => `
                      <tr>
                        <td class="text-right">${formatCurrency(tax.taxableAmount)}</td>
                        <td class="text-center">${tax.gstRate}%</td>
                        ${isInterStateTxn ? `
                          <td class="text-right">${formatCurrency(tax.taxAmount)}</td>
                        ` : `
                          <td class="text-right">${formatCurrency(tax.taxAmount / 2)}</td>
                          <td class="text-right">${formatCurrency(tax.taxAmount / 2)}</td>
                        `}
                        <td class="text-right">${formatCurrency(tax.taxAmount)}</td>
                      </tr>
                    `).join('')}
                  </table>
                </div>

                <div class="totals-section">
                  <div class="total-row">
                    <span>Taxable Amount:</span>
                    <span>₹${formatCurrency(data.subtotal)}</span>
                  </div>
                  ${isInterStateTxn ? `
                    <div class="total-row">
                      <span>IGST:</span>
                      <span>₹${formatCurrency(data.taxAmount)}</span>
                    </div>
                  ` : `
                    <div class="total-row">
                      <span>CGST:</span>
                      <span>₹${formatCurrency(data.taxAmount / 2)}</span>
                    </div>
                    <div class="total-row">
                      <span>SGST:</span>
                      <span>₹${formatCurrency(data.taxAmount / 2)}</span>
                    </div>
                  `}
                  <div class="total-row grand-total">
                    <span>Invoice Total:</span>
                    <span>₹${formatCurrency(data.totalAmount)}</span>
                  </div>
                  <div class="amount-in-words">
                    <strong>In Words:</strong> ${this.numberToWords(data.totalAmount)} Only
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="footer-section">
                <div>
                  <strong>Terms:</strong> Payment within 30 days | Goods once sold will not be taken back<br/>
                  ${businessInfo.website ? `<strong>Website:</strong> ${businessInfo.website}` : ''}
                </div>
                <div class="signature-section">
                  <strong>For ${businessInfo.name || 'YOUR COMPANY'}</strong><br/><br/>
                  ___________________<br/>
                  Authorized Signatory
                </div>
              </div>

              <!-- Page Number for last page -->
              ${totalPages > 1 ? `
              <div class="page-footer" style="border-top: 1px solid #ccc; margin-top: 4px;">
                <div class="page-footer-content">
                  <div></div>
                  <div>
                    <strong>Page ${pageNumber} of ${totalPages}</strong>
                  </div>
                </div>
              </div>
              ` : ''}
              ` : ''}
            </div>
          </div>
        </div>
          `
        }).join('')}
      </body>
      </html>
    `
  }

  // Generate A4 invoice HTML
  private generateA4InvoiceHTML(data: PrintData): string {
    const { businessInfo } = this
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Invoice - ${data.billNumber}</title>
        <style>
          @media print {
            @page {
              margin: 20mm;
              size: A4;
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #333;
            margin: 0;
            padding: 20px;
          }
          
          .invoice-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          
          .business-info h1 {
            margin: 0 0 10px 0;
            color: #e97316;
            font-size: 24px;
          }
          
          .business-info p {
            margin: 2px 0;
            font-size: 11px;
          }
          
          .invoice-title {
            text-align: right;
          }
          
          .invoice-title h2 {
            margin: 0;
            font-size: 28px;
            color: #333;
          }
          
          .bill-details {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
          }
          
          .bill-to, .bill-info {
            width: 48%;
          }
          
          .bill-to h3, .bill-info h3 {
            margin: 0 0 10px 0;
            font-size: 14px;
            color: #e97316;
          }
          
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          
          .items-table th {
            background-color: #f8f9fa;
            border: 1px solid #ddd;
            padding: 12px 8px;
            text-align: left;
            font-weight: bold;
          }
          
          .items-table td {
            border: 1px solid #ddd;
            padding: 10px 8px;
          }
          
          .items-table .text-right {
            text-align: right;
          }
          
          .items-table .text-center {
            text-align: center;
          }
          
          .totals-table {
            width: 300px;
            margin-left: auto;
            border-collapse: collapse;
          }
          
          .totals-table td {
            padding: 8px;
            border-bottom: 1px solid #eee;
          }
          
          .totals-table .total-row {
            font-weight: bold;
            font-size: 14px;
            border-top: 2px solid #333;
          }
          
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 11px;
            color: #666;
          }
          
          .terms {
            margin-top: 30px;
            border-top: 1px solid #eee;
            padding-top: 15px;
          }
          
          .terms h4 {
            margin: 0 0 10px 0;
            font-size: 12px;
          }
          
          .terms ul {
            margin: 0;
            padding-left: 20px;
            font-size: 10px;
          }
        </style>
      </head>
      <body>
        <!-- Invoice Header -->
        <div class="invoice-header">
          <div class="business-info">
            <h1>${businessInfo.name}</h1>
            ${businessInfo.address ? `<p>${businessInfo.address.replace(/\n/g, '<br>')}</p>` : ''}
            ${businessInfo.phone ? `<p>Phone: ${businessInfo.phone}</p>` : ''}
            ${businessInfo.email ? `<p>Email: ${businessInfo.email}</p>` : ''}
            ${businessInfo.gstNumber ? `<p>GSTIN: ${businessInfo.gstNumber}</p>` : ''}
          </div>
          <div class="invoice-title">
            <h2>${data.type === 'invoice' ? 'TAX INVOICE' : 'RECEIPT'}</h2>
          </div>
        </div>

        <!-- Bill Details -->
        <div class="bill-details">
          <div class="bill-to">
            <h3>Bill To:</h3>
            ${data.customerName ? `<p><strong>${data.customerName}</strong></p>` : '<p>Walk-in Customer</p>'}
            ${data.customerPhone ? `<p>Phone: ${data.customerPhone}</p>` : ''}
            ${data.customerGST ? `<p>GSTIN: ${data.customerGST}</p>` : ''}
          </div>
          <div class="bill-info">
            <h3>Invoice Details:</h3>
            <p><strong>Invoice No:</strong> ${data.billNumber}</p>
            <p><strong>Date:</strong> ${data.date}</p>
            <p><strong>Payment Method:</strong> ${data.paymentMethod || 'Cash'}</p>
          </div>
        </div>

        <!-- Items Table -->
        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 50%">Item Description</th>
              <th class="text-center" style="width: 10%">Qty</th>
              <th class="text-right" style="width: 15%">Rate</th>
              <th class="text-right" style="width: 15%">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${data.items.map(item => `
              <tr>
                <td>${item.name}</td>
                <td class="text-center">${item.quantity}</td>
                <td class="text-right">${formatCurrency(item.price)}</td>
                <td class="text-right">${formatCurrency(item.total)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <!-- Totals -->
        <table class="totals-table">
          <tr>
            <td>Subtotal:</td>
            <td class="text-right">${formatCurrency(data.subtotal)}</td>
          </tr>
          <tr>
            <td>GST (18%):</td>
            <td class="text-right">${formatCurrency(data.taxAmount)}</td>
          </tr>
          <tr class="total-row">
            <td>Total Amount:</td>
            <td class="text-right">${formatCurrency(data.totalAmount)}</td>
          </tr>
          ${data.amountReceived ? `
            <tr>
              <td>Amount Received:</td>
              <td class="text-right">${formatCurrency(data.amountReceived)}</td>
            </tr>
          ` : ''}
          ${data.change && data.change > 0 ? `
            <tr>
              <td>Change Given:</td>
              <td class="text-right">${formatCurrency(data.change)}</td>
            </tr>
          ` : ''}
        </table>

        <!-- Terms and Conditions -->
        <div class="terms">
          <h4>Terms & Conditions:</h4>
          <ul>
            <li>Goods once sold will not be taken back or exchanged</li>
            <li>All disputes are subject to local jurisdiction only</li>
            <li>Payment terms: Immediate payment required</li>
            <li>Please check goods before leaving the premises</li>
          </ul>
        </div>

        <!-- Footer -->
        <div class="footer">
          <p>Thank you for your business! 🪔</p>
          <p>Happy Diwali & Prosperous New Year!</p>
          ${businessInfo.website ? `<p>${businessInfo.website}</p>` : ''}
        </div>
      </body>
      </html>
    `
  }

  // Print receipt or invoice
  async printDocument(data: PrintData): Promise<void> {
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      throw new Error('Unable to open print window. Please check popup blocker settings.')
    }

    let htmlContent: string

    switch (this.settings.format) {
      case 'a4':
        htmlContent = this.generateA4InvoiceHTML(data)
        break
      case 'thermal':
        htmlContent = this.generateThermalReceiptHTML(data)
        break
      case 'a5':
        htmlContent = this.generateA5InvoiceHTML(data)
        break
      default:
        htmlContent = this.generateThermalReceiptHTML(data)
    }

    printWindow.document.write(htmlContent)
    printWindow.document.close()

    // Wait for content to load before printing
    setTimeout(() => {
      printWindow.focus()
      printWindow.print()
      
      // Close window after printing (with delay for print dialog)
      setTimeout(() => {
        printWindow.close()
      }, 1000)
    }, 500)
  }

  // Test print functionality
  async testPrint(): Promise<void> {
    const testData: PrintData = {
      type: 'invoice',
      billNumber: 'TEST-001',
      date: new Date().toLocaleDateString('en-IN'),
      customerName: 'Test Customer Pvt Ltd',
      customerPhone: '+91 98765 43210',
      customerEmail: 'customer@example.com',
      customerAddress: '456, Test Street, Test City - 600002',
      customerGST: '33YYYYY5678Y1Z5',
      customerState: 'Tamil Nadu',
      items: [
        {
          name: 'Product Item 1',
          quantity: 10,
          price: 1000,
          total: 11800,
          hsn: '9987',
          mrp: 1200,
          discount: 10,
          gstRate: 18
        },
        {
          name: 'Product Item 2',
          quantity: 5,
          price: 2000,
          total: 11200,
          hsn: '8471',
          mrp: 2500,
          discount: 5,
          gstRate: 12
        },
        {
          name: 'Product Item 3',
          quantity: 3,
          price: 500,
          total: 1770,
          hsn: '9987',
          mrp: 600,
          discount: 0,
          gstRate: 18
        }
      ],
      subtotal: 13500,
      taxAmount: 2270,
      totalAmount: 15770,
      paymentMethod: 'cash',
      amountReceived: 16000,
      change: 230
    }

    await this.printDocument(testData)
  }

  // Check if printer is available (Web API)
  async isPrinterAvailable(): Promise<boolean> {
    try {
      if ('serviceWorker' in navigator && 'print' in window) {
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  // Get available printers (if supported)
  async getAvailablePrinters(): Promise<string[]> {
    // This is a placeholder - actual printer detection would require
    // browser permissions or electron APIs
    return ['Default Printer', 'Thermal Printer (POS-80)', 'Network Printer']
  }
}

export const printerService = new PrinterService()
export default printerService